import api from "./api";

const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:5000";
const UPLOAD_PATH = process.env.REACT_APP_UPLOADS_PATH || "/api/uploads";

function normalizePath(path) {
  const value = String(path || "").trim();
  if (!value) return "/api/uploads";
  return value.startsWith("/") ? value : `/${value}`;
}

function getUploadFilePath() {
  return `${normalizePath(UPLOAD_PATH)}/file`;
}

function isUploadFilePath(pathname) {
  const path = String(pathname || "").replace(/\/+$/, "");
  const expected = getUploadFilePath().replace(/\/+$/, "");
  return path === expected || path.endsWith("/uploads/file");
}

/**
 * Always target the configured API for /api/uploads/file?key=…
 * (fixes DB rows that still store http://localhost:5000/... from dev uploads).
 */
export function resolveFileUrlToAbsolute(fileUrl) {
  const s = String(fileUrl || "").trim();
  if (!s) return "";
  const apiBase = API_BASE_URL.replace(/\/$/, "");

  try {
    const parsed = new URL(s, `${apiBase}/`);
    if (isUploadFilePath(parsed.pathname)) {
      const key = parsed.searchParams.get("key");
      if (key) {
        return `${apiBase}${getUploadFilePath()}?key=${encodeURIComponent(key)}`;
      }
    }
  } catch {
    // fall through
  }

  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) {
    return `${apiBase}${s}`;
  }
  return s;
}

/** Prefer storage key when URL points at the wrong host (e.g. localhost in production DB). */
export function resolveFileReference(fileUrl, storageKey) {
  const key = String(storageKey || "").trim();
  if (key) {
    return resolveFileUrlToAbsolute(`${getUploadFilePath()}?key=${encodeURIComponent(key)}`);
  }
  return resolveFileUrlToAbsolute(fileUrl);
}

function isBlobUrl(url) {
  return String(url || "").startsWith("blob:");
}

export function revokeObjectUrl(url) {
  if (isBlobUrl(url) && typeof URL !== "undefined") {
    URL.revokeObjectURL(url);
  }
}

export function isProtectedUploadUrl(fileUrl) {
  if (!fileUrl || typeof window === "undefined") return false;
  try {
    const resolved = resolveFileUrlToAbsolute(fileUrl);
    const parsedUrl = new URL(resolved);
    return isUploadFilePath(parsedUrl.pathname);
  } catch {
    return false;
  }
}

export async function fetchAuthorizedFileBlobUrl(fileUrl, storageKey) {
  const resolved = resolveFileReference(fileUrl, storageKey);
  if (!resolved) return "";
  if (!isProtectedUploadUrl(resolved)) {
    return resolved;
  }

  const token = api.getToken?.();
  // Public-read keys (e.g. caregiver/profile-image/) work as plain <img src>.
  // Without a token, return the URL so marketplace/customer profiles can load photos.
  if (!token) {
    return resolved;
  }

  try {
    const response = await fetch(resolved, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Fall back to direct URL (public-read profile images still load in <img>).
      return resolved;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return resolved;
  }
}

export async function uploadFileToS3(file, { folder = "caregiver-documents" } = {}) {
  if (!file) return null;

  const token = api.getToken?.();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", folder);

  const uploadResponse = await fetch(`${API_BASE_URL}${normalizePath(UPLOAD_PATH)}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!uploadResponse.ok) {
    if (uploadResponse.status === 404) {
      throw new Error("Upload API is not configured yet.");
    }
    let message = "Unable to upload file right now.";
    try {
      const payload = await uploadResponse.json();
      if (payload?.message) message = payload.message;
    } catch {
      // ignore non-json errors
    }
    throw new Error(message);
  }

  const payload = await uploadResponse.json();
  const uploaded = payload?.file || payload;

  return {
    fileName: uploaded?.fileName || file.name,
    contentType: uploaded?.contentType || file.type || "application/octet-stream",
    url: uploaded?.fileUrl || "",
    key: uploaded?.key || null,
  };
}
